/**
 * Catalog Formatting Utilities
 * Functions for formatting bibliographic data for display
 */

import { formatNumberWithCommas, parseFormattedNumber } from "@/lib/formatters";

export { formatNumberWithCommas, parseFormattedNumber } from "@/lib/formatters";

/**
 * Format physical description from structured fields into a merged string
 * @param pages - Number of pages
 * @param illustrations - Illustration description (e.g., "ILL", "col. ill.")
 * @param size - Size/preliminary matter (e.g., "iv", "24 cm")
 * @returns Formatted physical description (e.g., "iv, 75pg, ILL")
 */
export function formatPhysicalDescription(
  pages?: number,
  illustrations?: string,
  size?: string
): string {
  const parts: string[] = [];

  // Add size first (roman numerals or dimensions)
  if (size?.trim()) {
    parts.push(size.trim());
  }

  // Add pages
  if (pages && pages > 0) {
    parts.push(`${formatNumberWithCommas(pages)}pg`);
  }

  // Add illustrations
  if (illustrations?.trim()) {
    parts.push(illustrations.trim());
  }

  return parts.join(", ");
}

/**
 * Format title with subtitle for display
 * @param title - Main title
 * @param subtitle - Subtitle (optional)
 * @returns Formatted title (e.g., "Title: Subtitle" or just "Title")
 */
export function formatTitleWithSubtitle(title: string, subtitle?: string): string {
  if (!subtitle?.trim()) {
    return title;
  }
  return `${title}: ${subtitle}`;
}

/**
 * Format title with edition for display
 * @param title - Main title
 * @param subtitle - Subtitle (optional)
 * @param edition - Edition statement (optional)
 * @returns Formatted title (e.g., "Title: Subtitle (2nd ed)" or "Title (2nd ed)" or just "Title")
 */
export function formatTitleWithEdition(title: string, subtitle?: string, edition?: string): string {
  let result = formatTitleWithSubtitle(title, subtitle);

  if (edition?.trim()) {
    result += ` (${edition})`;
  }

  return result;
}

/**
 * Format author names in uppercase with proper formatting
 * Converts to "SURNAME, Initial" format if needed
 * @param author - Author name
 * @returns Formatted author name (e.g., "MODO, I")
 */
export function formatAuthorName(author: string): string {
  if (!author?.trim()) return "";

  // If already in uppercase, return as is
  if (author === author.toUpperCase()) {
    return author;
  }

  // Simple conversion to uppercase (librarian will handle proper formatting)
  return author.toUpperCase();
}

/**
 * Format call number to uppercase (LC standard)
 * @param callNumber - Call number
 * @returns Formatted call number (e.g., "DT515.83.P2")
 */
export function formatCallNumber(callNumber?: string): string {
  if (!callNumber?.trim()) return "";
  return callNumber.toUpperCase();
}

/**
 * Format accession number with commas
 * @param accessionNumber - Accession number
 * @returns Formatted accession number (e.g., "12,345")
 */
export function formatAccessionNumber(accessionNumber?: number): string {
  if (!accessionNumber) return "";
  return formatNumberWithCommas(accessionNumber);
}

/**
 * Parse accession number (remove commas)
 * @param value - Formatted accession number string
 * @returns Numeric accession number
 */
export function parseAccessionNumber(value: string): number | undefined {
  return parseFormattedNumber(value);
}

/**
 * Get acquisition mode label
 * @param code - Acquisition mode code
 * @returns Human-readable label
 */
export function getAcquisitionModeLabel(code?: string): string {
  if (!code) return "";

  const modes: Record<string, string> = {
    PURCHASE: "Purchase",
    DONATION: "Donation",
    GIFT: "Gift",
    EXCHANGE: "Exchange",
    TETFUND: "TETFund",
  };

  return modes[code] || code;
}
